import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { Grade } from "../model/Grade";
import { getGrades } from "../repositories/GradeRepository";

interface GradeOption {
  GradeId: number;
  GradeName: string;
}

const ALL_OPTION: GradeOption = { GradeId: 0, GradeName: "ALL" };

interface GradeFilterSelectorProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

export const GradeFilterSelector = ({
  value,
  onChange,
  disabled = false,
}: GradeFilterSelectorProps) => {
  const [grades, setGrades] = useState<GradeOption[]>([ALL_OPTION]);
  const [selectedGrade, setSelectedGrade] = useState<GradeOption>(ALL_OPTION);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    try {
      const resp = await getGrades(false);
      if (resp.ok) {
        const rows = get(resp, "data.tables[0].rows", []) as Grade[];
        const enabledGrades: GradeOption[] = rows
          .filter((g) => g.Enabled)
          .sort((a, b) => a.GradeName.localeCompare(b.GradeName))
          .map((g) => ({ GradeId: g.GradeId!, GradeName: g.GradeName }));
        setGrades([ALL_OPTION, ...enabledGrades]);
      } else {
        setError(resp.message);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while loading grades",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (value === 0) {
      setSelectedGrade(ALL_OPTION);
    } else {
      const grade = grades.find((g) => g.GradeId === value);
      setSelectedGrade(grade ?? ALL_OPTION);
    }
  }, [value, grades]);

  return (
    <>
      <Autocomplete
        options={grades}
        getOptionLabel={(option) => option.GradeName}
        value={selectedGrade}
        onChange={(_event, newValue) => {
          const selected = newValue ?? ALL_OPTION;
          setSelectedGrade(selected);
          onChange(selected.GradeId);
        }}
        isOptionEqualToValue={(option, val) => option.GradeId === val.GradeId}
        disableClearable
        disabled={disabled || isLoading}
        loading={isLoading}
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Grade"
            variant="outlined"
            fullWidth
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error loading Grades"
      />
    </>
  );
};
